import { createTheme, lighten, type Theme } from '@mui/material/styles';

// Theme presets (named palettes) + a primary-color picker, à la Aurora. Each
// preset supplies a primary colour; the built theme carries both light & dark
// colour schemes (MUI v6 CSS variables), so mode (light/dark/system) is a
// separate axis handled by useColorScheme().

export interface Preset {
  key: string;
  name: string;
  primary: string;   // brand/primary colour
  accent: string;    // a secondary swatch shown in the preview chip
}

export const PRESETS: Preset[] = [
  { key: 'default', name: 'Default', primary: '#0b7a75', accent: '#23b3ab' },
  { key: 'luxury', name: 'Luxury', primary: '#9a1f2b', accent: '#c9a227' },
  { key: 'retro', name: 'Retro', primary: '#2b3a55', accent: '#d9c3a0' },
  { key: 'arctic', name: 'Arctic', primary: '#0e7490', accent: '#bae6fd' },
  { key: 'nature', name: 'Nature', primary: '#2e7d32', accent: '#a3e635' },
  { key: 'ember', name: 'Ember', primary: '#c2410c', accent: '#f59e0b' },
  { key: 'dracula', name: 'Dracula', primary: '#7c3aed', accent: '#ec4899' },
  { key: 'midnight', name: 'Midnight', primary: '#1e3a8a', accent: '#3b82f6' },
];

export const PRIMARY_SWATCHES = [
  '#0b7a75', '#3b82f6', '#1e3a8a', '#9a1f2b', '#0e7490', '#2e7d32', '#c2410c', '#7c3aed', '#6366f1',
];

const shared = {
  shape: { borderRadius: 8 },
  typography: { fontFamily: '-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Helvetica,Arial,sans-serif' },
  components: {
    MuiButton: { defaultProps: { disableElevation: true }, styleOverrides: { root: { textTransform: 'none' as const, fontWeight: 600 } } },
  },
} as const;

/** Build a theme for a preset (with an optional primary-colour override). Carries
 *  both light and dark colour schemes; the active mode is chosen by the provider. */
export function buildTheme(presetKey: string, primaryOverride?: string): Theme {
  const preset = PRESETS.find((p) => p.key === presetKey) ?? PRESETS[0]!;
  const primary = primaryOverride || preset.primary;
  return createTheme({
    cssVariables: { colorSchemeSelector: 'class' },
    colorSchemes: {
      light: {
        palette: {
          primary: { main: primary },
          secondary: { main: preset.accent },
          background: { default: '#f6f7f9', paper: '#ffffff' },
          divider: '#e5e8ec',
        },
      },
      dark: {
        palette: {
          primary: { main: lighten(primary, 0.2) },
          secondary: { main: preset.accent },
          background: { default: '#0b1020', paper: '#131a24' },
          divider: '#1e293b',
        },
      },
    },
    ...shared,
  });
}
